import apiClient from './client';
import type { PaginatedResponse } from '../types';

export interface SkillWithStats {
  id: string;
  name: string;
  category: string | null;
  taxonomy_id: string | null;
  employee_count: number;
  project_report_count: number;
  tender_count: number;
  endorsement_count: number;
  total_references: number;
  created_at: string;
}

export interface SkillAdminParams {
  page?: number;
  page_size?: number;
  search?: string;
  category?: string;
  sort_by?: string;
  order?: string;
}

const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  category: 'category',
  total_references: 'totalReferences',
};

export async function fetchSkillsAdmin(
  params: SkillAdminParams = {},
): Promise<PaginatedResponse<SkillWithStats>> {
  const sortKey = params.sort_by ?? 'name';
  const order = params.order ?? 'asc';
  const springSort = `${SORT_FIELD_MAP[sortKey] ?? 'name'},${order}`;

  const springParams: Record<string, unknown> = {
    page: (params.page ?? 1) - 1,
    size: params.page_size ?? 20,
    sort: springSort,
    ...(params.search ? { search: params.search } : {}),
    ...(params.category ? { category: params.category } : {}),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await apiClient.get<any>('/admin/skills', { params: springParams });

  return {
    items: data.content ?? [],
    total: data.total_elements ?? 0,
    page: (data.number ?? 0) + 1,
    page_size: data.size ?? 20,
    total_pages: data.total_pages ?? 0,
  };
}

export async function fetchSkillCategories(): Promise<string[]> {
  const { data } = await apiClient.get<string[]>('/admin/skills/categories');
  return data;
}

export async function updateSkill(
  id: string,
  body: { name: string; category: string | null },
): Promise<SkillWithStats> {
  const { data } = await apiClient.put<SkillWithStats>(`/admin/skills/${id}`, body);
  return data;
}

export async function deleteSkill(id: string): Promise<void> {
  await apiClient.delete(`/admin/skills/${id}`);
}

export async function mergeSkills(
  targetId: string,
  sourceIds: string[],
): Promise<SkillWithStats> {
  const { data } = await apiClient.post<SkillWithStats>('/admin/skills/merge', {
    target_id: targetId,
    source_ids: sourceIds,
  });
  return data;
}
